import type { SubmitHandler } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { Button, ColorTag, Labeled, Stack, Step } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IPackagePreparerStepProps } from './PackagePreparerStep';
import { FormField } from '~/components/atoms/FormField';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { CustomColorInputField } from '~/components/organisms/fields/CustomColorInputField';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';

export type IPackagePreparerAndroidThemeStepProps = IPackagePreparerStepProps;

export type IPackagePreparerAndroidThemeStepFormValue = {
  splashScreenBackgroundColor?: string;
};

const styles = stylex.create({
  field: {
    width: 400,
  },
});

export const PackagePreparerAndroidThemeStep: React.FC<
  IPackagePreparerAndroidThemeStepProps
> = (props) => {
  const { appId, onSuccess, ...other } = props;
  const { t } = useTranslation();
  const appQuery = useAppQuery({ appId });
  const updateAppMutation = useUpdateAppMutation();
  const form = useForm<IPackagePreparerAndroidThemeStepFormValue>();
  const app = appQuery.data;
  const splashScreenBackgroundColor =
    app?.builds?.android?.splashScreenBackgroundColor;

  const isInitRef = useRef(false);
  const completed = !!splashScreenBackgroundColor;
  useEffect(() => {
    if (!app || isInitRef.current) {
      return;
    }

    isInitRef.current = true;
    if (completed) {
      onSuccess?.({
        source: 'init',
      });
    }
  }, [app, completed, onSuccess]);

  const handleSubmit: SubmitHandler<
    IPackagePreparerAndroidThemeStepFormValue
  > = (values) => {
    if (updateAppMutation.isLoading) {
      return;
    }

    return updateAppMutation.mutateAsync(
      {
        appId,
        body: {
          androidSplashScreenBackgroundColor:
            values.splashScreenBackgroundColor,
        },
      },
      {
        onSuccess: onSuccess ? () => onSuccess({ source: 'user' }) : undefined,
      },
    );
  };

  return (
    <Step
      {...other}
      label={t('PackagePreparerAndroidThemeStep.name')}
      hasError={!!appQuery.error}
      alwaysExpanded={!other.disabled}
    >
      {appQuery.isError ? (
        <ErrorMessage error={appQuery.error} />
      ) : appQuery.isLoading ? (
        t('common.loading')
      ) : app ? (
        ({ active }) =>
          active ? (
            <Stack
              as="form"
              gap={6}
              align="start"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <Labeled
                label={t(
                  'PackagePreparerAndroidThemeStep.field.splashScreenBackgroundColor.label',
                )}
                required
              >
                <FormField
                  name="splashScreenBackgroundColor"
                  control={form.control}
                  as={CustomColorInputField}
                  forwardError
                  sx={styles.field}
                  variant="outlined"
                  defaultValue={splashScreenBackgroundColor}
                  palette="app"
                />
              </Labeled>
              <Button type="submit" loading={updateAppMutation.isLoading}>
                {t('common.next')}
              </Button>
            </Stack>
          ) : splashScreenBackgroundColor ? (
            <Stack horizontal gap={2}>
              <ColorTag
                backgroundColor={splashScreenBackgroundColor}
                label={splashScreenBackgroundColor}
              />
              {t(
                'PackagePreparerAndroidThemeStep.summary.splashScreenBackgroundColor',
              )}
            </Stack>
          ) : null
      ) : null}
    </Step>
  );
};

PackagePreparerAndroidThemeStep.displayName = 'Step';
